import React, { useEffect, useRef, useCallback, useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const DEFAULT_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI';

// Cache for captcha config
let captchaConfigCache = null;
let captchaCacheTime = 0;
const CACHE_TTL = 60000; // 60 seconds

export const getCaptchaConfig = async () => {
  const now = Date.now();
  if (captchaConfigCache && (now - captchaCacheTime) < CACHE_TTL) return captchaConfigCache;
  try {
    const res = await axios.get(`${API}/public/captcha-config`);
    captchaConfigCache = res.data;
    captchaCacheTime = now;
    return captchaConfigCache;
  } catch {
    return { enabled: false };
  }
};

const ReCaptcha = ({ onVerify, onExpire, onError, siteKey }) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const resolvedKey = siteKey || DEFAULT_SITE_KEY;
  const [loadError, setLoadError] = useState(false);

  const handleVerify = useCallback((token) => {
    if (onVerify) onVerify(token);
  }, [onVerify]);

  const handleExpire = useCallback(() => {
    if (onExpire) onExpire();
  }, [onExpire]);

  const handleError = useCallback((err) => {
    console.warn('reCAPTCHA error (non-blocking):', err);
    setLoadError(true);
    if (onError) onError(err);
  }, [onError]);

  useEffect(() => {
    // Catch reCAPTCHA global errors silently
    const prevOnError = window.onerror;
    window.onerror = (msg, src, line, col, err) => {
      if (msg === 'Timeout' || (typeof msg === 'string' && msg.includes('recaptcha'))) {
        console.warn('reCAPTCHA global error suppressed:', msg);
        return true;
      }
      if (prevOnError) return prevOnError(msg, src, line, col, err);
      return false;
    };

    if (!window.grecaptcha) {
      const script = document.createElement('script');
      script.src = 'https://www.google.com/recaptcha/api.js?render=explicit';
      script.async = true;
      script.defer = true;
      script.onload = () => renderCaptcha();
      script.onerror = () => setLoadError(true);
      document.head.appendChild(script);
    } else {
      renderCaptcha();
    }

    function renderCaptcha() {
      if (window.grecaptcha && window.grecaptcha.render && containerRef.current) {
        if (widgetIdRef.current !== null) return;
        try {
          widgetIdRef.current = window.grecaptcha.render(containerRef.current, {
            sitekey: resolvedKey,
            callback: handleVerify,
            'expired-callback': handleExpire,
            'error-callback': handleError,
            theme: 'dark',
          });
        } catch (e) {
          console.warn('reCAPTCHA render error:', e);
          setLoadError(true);
        }
      }
    }

    return () => {
      widgetIdRef.current = null;
      window.onerror = prevOnError;
    };
  }, [handleVerify, handleExpire, handleError, resolvedKey]);

  if (loadError) {
    return (
      <div className="text-center p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
        <p className="text-yellow-400 text-sm">CAPTCHA indisponible sur ce domaine. Le formulaire reste utilisable.</p>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="flex justify-center" data-testid="recaptcha-container" />
  );
};

export const useCaptcha = () => {
  const [captchaToken, setCaptchaToken] = useState(null);
  const [captchaKey, setCaptchaKey] = useState(0);
  const [captchaConfig, setCaptchaConfig] = useState(null);
  const [captchaFailed, setCaptchaFailed] = useState(false);

  useEffect(() => {
    getCaptchaConfig().then(setCaptchaConfig);
  }, []);

  const resetCaptcha = useCallback(() => {
    setCaptchaToken(null);
    setCaptchaKey(prev => prev + 1);
    if (window.grecaptcha) {
      try { window.grecaptcha.reset(); } catch {}
    }
  }, []);

  const onVerify = useCallback((token) => { setCaptchaToken(token); }, []);
  const onExpire = useCallback(() => { setCaptchaToken(null); }, []);
  const onError = useCallback(() => { setCaptchaFailed(true); }, []);

  return {
    captchaToken,
    captchaKey,
    resetCaptcha,
    onVerify,
    onExpire,
    onError,
    isVerified: !!captchaToken || captchaFailed,
    captchaConfig,
    captchaFailed,
  };
};

export default ReCaptcha;
